import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ArrowRight, User, Star, Archive, ArchiveRestore } from 'lucide-react';
import { getCategoryMeta } from '@/lib/workflowCategories';
import type { WorkflowSummary } from '@/hooks/useRealtimeWorkflows';

interface WorkflowCardProps {
  workflow: WorkflowSummary;
  // Where the card links to — the manager detail page or the run page.
  to: string;
  // Show the draft/published status badge (manager view).
  showStatus?: boolean;
  // Render as a compact list row instead of a card.
  view?: 'grid' | 'list';
  // Favorites (optional — omit to hide the star).
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  // Archive control (optional — omit to hide the archive button).
  onToggleArchive?: (id: string) => void;
}

function FavoriteStar({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      className="p-1 rounded-md hover:bg-accent transition-colors shrink-0"
      title={active ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Star
        className={
          'h-4 w-4 transition-colors ' +
          (active ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30 hover:text-amber-400')
        }
      />
    </button>
  );
}

function ArchiveButton({ hidden, onToggle }: { hidden: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      className="p-1 rounded-md hover:bg-accent transition-colors shrink-0"
      title={hidden ? 'Unarchive workflow' : 'Archive workflow'}
    >
      {hidden ? (
        <ArchiveRestore className="h-4 w-4 text-muted-foreground/60 hover:text-foreground transition-colors" />
      ) : (
        <Archive className="h-4 w-4 text-muted-foreground/60 hover:text-foreground transition-colors" />
      )}
    </button>
  );
}

function CategoryChips({ categories }: { categories: string[] }) {
  if (categories.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {categories.map((cat) => {
        const meta = getCategoryMeta(cat);
        if (!meta) return null;
        const Icon = meta.icon;
        return (
          <span
            key={cat}
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground"
          >
            <Icon className="h-2.5 w-2.5" />
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

export default function WorkflowCard({
  workflow: wf,
  to,
  showStatus = false,
  view = 'grid',
  isFavorite,
  onToggleFavorite,
  onToggleArchive,
}: WorkflowCardProps) {
  const showFav = isFavorite !== undefined && !!onToggleFavorite;
  const showArchive = !!onToggleArchive;

  if (view === 'list') {
    return (
      <Link to={to}>
        <div
          className={
            'group flex items-center gap-4 rounded-xl border border-border/60 bg-card px-4 py-3 transition-all ' +
            'hover:border-violet-400/40 hover:bg-accent/30'
          }
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-100 dark:bg-violet-500/20 shrink-0">
            <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className={
                  'text-sm font-medium text-card-foreground truncate ' +
                  'group-hover:text-violet-700 dark:group-hover:text-violet-400 transition-colors'
                }
              >
                {wf.name}
              </h3>
              {wf.hidden === true && (
                <Badge variant="outline" className="text-[10px] flex items-center gap-1 shrink-0">
                  <Archive className="h-2.5 w-2.5" />
                  Archived
                </Badge>
              )}
              {showStatus &&
                (wf.status === 'published' ? (
                  <Badge className="text-[10px] bg-green-600 shrink-0">Published</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    Draft
                  </Badge>
                ))}
            </div>
            {wf.description && (
              <p className="text-xs text-muted-foreground truncate" title={wf.description}>
                {wf.description}
              </p>
            )}
          </div>
          <div className="hidden md:block shrink-0">
            <CategoryChips categories={wf.categories} />
          </div>
          <span className="text-[11px] text-muted-foreground/60 shrink-0 hidden sm:flex items-center gap-1 max-w-[140px]">
            {wf.ownerName && (
              <>
                <User className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate">{wf.ownerName}</span>
              </>
            )}
          </span>
          <span className="text-[11px] text-muted-foreground/60 shrink-0 w-20 text-right">
            {new Date(wf.createdAt).toLocaleDateString()}
          </span>
          {showArchive && <ArchiveButton hidden={wf.hidden === true} onToggle={() => onToggleArchive!(wf.id)} />}
          {showFav && <FavoriteStar active={!!isFavorite} onToggle={() => onToggleFavorite!(wf.id)} />}
          <ArrowRight className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </Link>
    );
  }

  return (
    <Link to={to} className="h-full">
      <div
        className={
          'group relative rounded-2xl border border-border/60 bg-card p-5 transition-all duration-300 h-full ' +
          'hover:shadow-xl hover:shadow-violet-500/5 hover:border-violet-400/40 hover:-translate-y-0.5 ' +
          'dark:hover:shadow-violet-500/10 flex flex-col'
        }
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-100 dark:bg-violet-500/20 shrink-0">
            <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex items-center gap-1.5">
            {wf.hidden === true && (
              <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                <Archive className="h-2.5 w-2.5" />
                Archived
              </Badge>
            )}
            {showStatus &&
              (wf.status === 'published' ? (
                <Badge className="text-[10px] bg-green-600">Published</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">
                  Draft
                </Badge>
              ))}
            {showArchive && <ArchiveButton hidden={wf.hidden === true} onToggle={() => onToggleArchive!(wf.id)} />}
            {showFav && <FavoriteStar active={!!isFavorite} onToggle={() => onToggleFavorite!(wf.id)} />}
          </div>
        </div>

        <h3
          className={
            'text-base font-semibold text-card-foreground mb-1 truncate ' +
            'group-hover:text-violet-700 dark:group-hover:text-violet-400 transition-colors'
          }
        >
          {wf.name}
        </h3>
        <p className="text-xs text-muted-foreground flex-1 line-clamp-2" title={wf.description || undefined}>
          {wf.description || `${wf.outputNodeCount} ${wf.outputNodeCount === 1 ? 'output' : 'outputs'}`}
        </p>

        <div className="mt-3">
          <CategoryChips categories={wf.categories} />
        </div>

        <div className="flex items-center justify-between pt-3 mt-3 border-t border-border/40">
          <span className="text-[11px] text-muted-foreground/60 truncate flex items-center gap-1 min-w-0">
            {wf.ownerName && (
              <>
                <User className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate">{wf.ownerName}</span>
                <span className="shrink-0">·</span>
              </>
            )}
            <span className="shrink-0">{new Date(wf.createdAt).toLocaleDateString()}</span>
          </span>
          <div
            className={
              'flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 ' +
              'opacity-0 group-hover:opacity-100 transition-all translate-x-1 group-hover:translate-x-0 shrink-0'
            }
          >
            {showStatus ? 'Edit' : 'Run'}
            <ArrowRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </Link>
  );
}
